import {autobind} from 'core-decorators';
import debug from 'debug';

import {DataSource} from '../../replete';
import {addAuthorizationHeaderIfPresent, isPromise, isStatic, isSticker} from './util';
import emitter from './util/emitter';
import type {ResponseAndBody} from './services/apiService';

const LIMIT = 50;

export const SEARCHFILTERS = {
    all: 'none',
    gifs: undefined,
    stickers: 'sticker',
    memes: 'static,-sticker',
};

export class MyDataSource extends DataSource {
    declare store: app.MyStore;
    console: app.MyStore['console'];

    constructor(store: app.MyStore, path: string) {
        super(store, path);
        this.console = store.console.addIdentifier({
            component: `replete:DataSource:${path}`,
        });
    }
}

export class AuthDataSource extends MyDataSource {
    constructor(store: app.MyStore, path: string) {
        super(store, path);
        emitter.on('authDataSet', this.eventHandler);
    }
    destruct() {
        super.destruct();
        emitter.off('authDataSet', this.eventHandler);
    }
    @autobind
    eventHandler() {
        const authData = this.store.authService.authData;
        const pending = this.store.authService.pending;
        this.console.debug('eventHandler', {authData, pending});
        this.set([], {
            authData,
            pending,
        });
    }
    getInitial(keyArgs) {
        const authData = this.store.authService.authData;
        const pending = this.store.authService.pending;
        return {
            authData,
            pending,
        };
    }
}


export class AppConfigDataSource extends MyDataSource {
    constructor(store: app.MyStore, path: string) {
        super(store, path);
        emitter.on('appConfigFetched', this.eventHandler);
    }
    destruct() {
        super.destruct();
        emitter.off('appConfigFetched', this.eventHandler);
    }
    @autobind
    eventHandler(event) {
        const [anonid, body] = event.detail;
        this.console.debug('appConfigFetched eventHandler', anonid, body);
        body.loaded = true;
        body.pending = false;
        this.set([anonid], body);
    }
    clean(val) {
        if (!isPromise(val.promise)) {
            val.promise = Promise.resolve(val);
        }
        return val;
    }
    key([anonid]) {
        return anonid;
    }
    isValidKeyArgs([anonid]) {
        return typeof anonid === 'string' || anonid instanceof String;
    }
    callApi() {
        return this.store.apiService.getAppConfig();
    }
    getData([anonid], currentVal) {
        currentVal.pending = true;
        this.set([anonid], currentVal);

        return this.callApi().then(({body, response}) => {
            body.loaded = true;
            body.pending = false;
            body.promise = Promise.resolve(body);
            this.set([anonid], body);
            return body;
        }, ({body, response}) => {
            body = body || currentVal;
            body.pending = false;
            body.status = response ? response.status : 0;
            body.promise = Promise.reject(body);
            this.set([anonid], body);
            return body;
        });
    }
    getInitial([anonid]) {
        const val = {
            pending: false,
            promise: null,
            flags: [],
        };
        this.store.request.console.debug('AppConfigDataSource getInitial', val);
        if (process.env.BROWSER && this.isValidKeyArgs([anonid])) {
            val.pending = true;
            val.promise = this.store.monitor(this.getData([anonid], val));
        } else {
            val.promise = Promise.resolve(val);
        }
        this.clean(val);
        return val;
    }
}


export class ConfigDataSource extends MyDataSource {
    constructor(store: app.MyStore, path: string) {
        super(store, path);
        emitter.on('configFetched', this.eventHandler);
    }
    destruct() {
        super.destruct();
        emitter.off('configFetched', this.eventHandler);
    }
    @autobind
    eventHandler(event) {
        const [username, body] = event.detail;
        this.console.debug('configFetched eventHandler', username, body);
        body.loaded = true;
        body.pending = false;
        this.set([username], body);
    }
    clean(val) {
        if (!isPromise(val.promise)) {
            val.promise = Promise.resolve(val);
        }
        return val;
    }
    key([username]) {
        return username || '';
    }
    isValidKeyArgs([username]) {
        return true;
    }
    callApi([username]) {
        return this.store.apiService.getConfig();
    }
    getData([username], currentVal) {
        currentVal.pending = true;
        this.set([username], currentVal);

        return this.callApi([username]).then(({body, response}) => {
            body.loaded = true;
            body.pending = false;
            body.promise = Promise.resolve(body);
            this.set([username], body);
            return body;
        }, ({body, response}) => {
            body = body || currentVal;
            body.pending = false;
            body.status = response ? response.status : 0;
            body.promise = Promise.reject(body);
            this.set([username], body);
            return body;
        });
    }
    getInitial([username]) {
        const val = {
            pending: true,
            promise: null,
            flags: [],
        };
        if (this.isValidKeyArgs([username])) {
            val.promise = this.store.monitor(this.getData([username], val));
        }
        return val;
    }
}


export class NotificationsDataSource extends MyDataSource {
    constructor(store: app.MyStore, path: string) {
        super(store, path);
        emitter.on('clearPendingNotificationsCount',
            this.clearPendingNotificationsCount);
        emitter.on('refreshNotifications', this.refresh);
    }
    destruct() {
        super.destruct();
        emitter.off('clearPendingNotificationsCount',
            this.clearPendingNotificationsCount);
        emitter.off('refreshNotifications', this.refresh);
    }
    @autobind
    refresh() {
        this.console.debug('refresh');
        const authService = this.store.authService;
        if (authService.isLoggedIn()) {
            this.getInitial([]);
        }
    }
    @autobind
    clearPendingNotificationsCount() {
        const val = {...this.get([])};
        this.set([], val);
    }
    key(keyArgs) {
        return '';
    }
    isValidKeyArgs(keyArgs) {
        return true;
    }
    callApi(next: string) {
        return this.store.apiService.getV2Notifications(next);
    }
    getData(currentVal) {
        this.set([], currentVal);

        return this.callApi(currentVal.next).then(({body, response}) => {
            // `pending`` is not present on v2, need to fake it on front end.
            let newPending = 0;
            const newNotifications = body.notifications || [];
            const oldNotifications = currentVal.notifications;
            const lastFetchedTS = oldNotifications[0]?.timestamp;

            // Set pending flag on new notifications. Also set html.
            for (let i = 0; i < newNotifications.length; i++) {
                if (lastFetchedTS &&
                    newNotifications[i].timestamp > lastFetchedTS) {
                    newNotifications[i].pending = true;
                    newPending++;
                } else {
                    break;
                }
            }

            body.notifications = [].concat(oldNotifications, newNotifications);
            body.pending = newPending + currentVal.pending;

            this.set([], body);
            return body;
        }, ({body, response}) => {
            this.set([], currentVal);
            return body;
        });
    }
    more(keyArgs) {
        const currentVal = this.get(keyArgs);
        if (currentVal.next) {
            this.getData(currentVal);
        }
    }
    getInitial(keyArgs) {
        const oneWeekAgo = 7 * 24 * 60 * 60;
        const val = {
            pending: 0,
            notifications: [],
            promise: null,
        };
        this.set([], val);
        const authService = this.store.authService;
        if (authService.isLoggedIn()) {
            if (this.isValidKeyArgs([])) {
                val.promise = this.store.monitor(this.getData(val));
            }
        }
        return val;
    }
}

export class TagsDataSource extends MyDataSource {
    key([type]) {
        return type;
    }
    getInitial([type]) {
        const val = {
            pending: true,
            tags: [],
            promise: null,
        };
        this.set([type], val);

        if (type) {
            const p = this.store.apiService.getV2<api.CategoryListBody>(
                '/categories', {type, contentfilter: 'high'});
            val.promise = this.store.monitor(p.then(({body, response}) => {
                const newBody = {
                    ...body,
                    pending: false,
                    tags: body.tags || [],
                    promise: null,
                };
                newBody.promise = Promise.resolve(newBody);
                this.set([type], newBody);
                return newBody;
            }, ({body, response}) => {
                body = body || val;
                body.pending = false;
                body.promise = Promise.reject(body);
                this.set([type], body);
                return body;
            }));
        }
        return val;
    }
}


export class SearchSuggestionsDataSource extends MyDataSource {
    key([q]) {
        return q;
    }
    getInitial([q]) {
        const val = {
            pending: true,
            results: [],
        };
        this.set([q], val);

        if (q) {
            const p = this.store.apiService.getV2<api.TagListBody>(
                '/search_suggestions', {q});
            this.store.monitor(p.then(({body, response}) => {
                body.pending = false;
                this.set([q], body);
                return body;
            }, ({body, response}) => {
                body = body || val;
                body.pending = false;
                this.set([q], body);
                return body;
            }));
        }
        return val;
    }
}


export class ExploreTermsDataSource extends MyDataSource {
    key([tag]) {
        return tag;
    }
    getPrefix(tag) {
        return /^[a-z]$/.test(tag[0]) ? tag[0] : '-';
    }
    callApi([tag]) {
        const url = `https://static.tenor.com/exploreterms/${this.getPrefix(tag)}/${tag || '-'}.json`;
        return this.store.httpService.request({
            method: 'GET',
            url,
            params: undefined,
            headers: new Headers(),
        }).then((response) => {
            if (response.ok) {
                return response.text().then((text) => {
                    return {body: JSON.parse(text), response};
                });
            }
            return Promise.reject({body: null, response});
        }, (err) => {
            return Promise.reject({body: {error: err}, response: null});
        });
    }
    isValidKeyArgs([tag]) {
        return tag || tag === '' ? true : false;
    }
    getData(keyArgs, currentVal) {
        currentVal.pending = true;
        this.set(keyArgs, currentVal);

        return this.callApi(keyArgs)
            .then(({body, response}) => {
                body.loaded = true;
                body.pending = false;
                body.results = body.results || [];
                this.set(keyArgs, body);
                return body;
            }, ({body, response}) => {
                body = body || currentVal;
                body.pending = false;
                body.results = [];
                body.status = response ? response.status : 0;
                this.set(keyArgs, body);
                return body;
            });
    }
    getInitial([tag]) {
        const val = {
            loaded: false,
            pending: true,
            results: [],
        };
        if (this.isValidKeyArgs([tag])) {
            this.store.monitor(this.getData([tag], val));
        }
        return val;
    }
}


export class GifResultsDataSource extends MyDataSource {
    addCommonParams(params) {
        params['media_filter'] = this.store.apiService.mediaFilterStr;
        params['fields'] = this.store.apiService.postResultsFieldsStr;
        return params;
    }
    callApi(keyArgs: string[], pos: string): Promise<
        ResponseAndBody<api.PostListBody>> {
        return Promise.reject({
            body: {error: 'callApi not defined'},
            response: null,
        });
    }
    isValidKeyArgs(keyArgs) {
        return keyArgs[0] ? true : false;
    }
    getData(keyArgs, currentVal) {
        const pos = currentVal.next;

        currentVal.pending = true;
        this.set(keyArgs, currentVal);
        this.console.debug('getData', keyArgs, pos);

        return this.callApi(keyArgs, pos).then(({body, response}) => {
            body = this.clean((body as api.PostListBody));
            // NOTE (v2 API): next = "" when no more results
            if (body.next === '') {
                body.noMore = true;
            }
            body.loaded = true;
            body.pending = false;
            let idx = currentVal.results.length;
            for (const result of body.results) {
                result.index = idx;
                idx++;
            }
            body.results = currentVal.results.concat(body.results || []);
            this.set(keyArgs, body);
            return body;
        }, ({body, response}) => {
            body = body || currentVal;
            body = this.clean(body);
            body.noMore = true;
            body.pending = false;
            body.results = [];
            this.set(keyArgs, body);
            return body;
        });
    }
    clean(val) {
        val.results = val.results || [];
        if (!isPromise(val.promise)) {
            val.promise = Promise.resolve(val);
        }
        return val;
    }
    getBlankData() {
        return this.clean({
            loaded: false,
            pending: true,
            noMore: false,
            promise: null,
        });
    }
    getInitial(keyArgs) {
        const val = this.getBlankData();
        if (this.isValidKeyArgs(keyArgs)) {
            val.promise = this.store.monitor(this.getData(keyArgs, val));
        }
        this.set(keyArgs, val);
        return val;
    }
    more(keyArgs) {
        const currentVal = this.get(keyArgs);
        if (!currentVal.pending && !currentVal.noMore) {
            this.getData(keyArgs, currentVal);
        }
    }
}


export class FeaturedGifsDataSource extends GifResultsDataSource {
    isValidKeyArgs(keyArgs) {
        return true;
    }
    callApi(keyArgs, pos) {
        const query = this.addCommonParams({limit: LIMIT});
        if (pos) {
            query.pos = pos;
        }
        return this.store.apiService.getV2<api.PostListBody>(
            '/featured', query);
    }
}


export class GifsDataSource extends GifResultsDataSource {
    addCommonParams(params) {
        params['media_filter'] = this.store.apiService.mediaFilterStr;
        params['fields'] = this.store.apiService.postResultFieldsStr;
        return params;
    }

    callApi([id]) {
        this.console.debug('callApi', id);
        if (!id) {
            return Promise.resolve<ResponseAndBody<api.PostListBody>>(
                {body: this.getBlankData(), response: null});
        }
        return this.store.apiService.getV2<api.PostListBody>(
            '/posts', this.addCommonParams({ids: id}));
    }
    key([id]) {
        this.console.debug('key', id);
        return id;
    }
}


export class RelatedGifsDataSource extends GifResultsDataSource {
    callApi([id]) {
        const anonId = this.store.apiService.getAnonid();
        return this.store.get('api.appConfig.*', [anonId]).promise.then((...args) => {
            const [appConfig] = args;
            const params = this.addCommonParams({id, contentfilter: 'medium'});
            if (appConfig.flags.web_universal_search_enabled) {
                params['searchfilter'] = SEARCHFILTERS.all;
            }
            return this.store.apiService.getV2<api.PostListBody>(
                '/related', params);
        });
    }
    key([id]) {
        return id;
    }
}


export class PacksDataSource extends GifResultsDataSource {
    clean(val) {
        val = super.clean(val);
        val.name = val.name || '';
        return val;
    }
    callApi([id], pos) {
        if (!id.match(/^[0-9a-z]+$/)) {
            return Promise.reject({
                body: {
                    error: `${id} is not a valid encrypted_v1_id`,
                    loaded: true,
                    pending: false,
                },
                response: null,
            });
        }
        const authService = this.store.authService;
        const googleAccessToken = authService.getGoogleAccessToken();

        const query = this.addCommonParams({
            encrypted_v1_id: id,
            limit: LIMIT,
        });
        const headers = new Headers();
        addAuthorizationHeaderIfPresent(headers, googleAccessToken);
        if (pos) {
            if (!Number.isInteger(pos)) {
                return Promise.reject({
                    body: {
                        error: `${pos} is not a valid pos`,
                    },
                    response: null,
                });
            }
            query.pos = pos;
        }
        return this.store.apiService.getV2<api.PostListBody>(
            '/collection_posts', query, headers);
    }
    key([id]) {
        const hasTenorScope = this.store.authService.hasTenorOAuthScope();
        return `${id}-${hasTenorScope || false}`;
    }
}


export class CollectionsDataSource extends GifResultsDataSource {
    clean(val) {
        val = super.clean(val);
        val.name = val.name || '';
        return val;
    }
    callApi([id], pos) {
        if (!id.match(/^[0-9a-z]+$/)) {
            return Promise.reject({
                body: {
                    error: `${id} is not a valid encrypted_v1_id`,
                    loaded: true,
                    pending: false,
                },
                response: null,
            });
        }
        const authService = this.store.authService;
        const googleAccessToken = authService.getGoogleAccessToken();

        const query = this.addCommonParams({
            encrypted_v1_id: id,
            limit: LIMIT,
        });
        const headers = new Headers();
        addAuthorizationHeaderIfPresent(headers, googleAccessToken);
        if (pos) {
            if (!Number.isInteger(pos)) {
                return Promise.reject({
                    body: {
                        error: `${pos} is not a valid pos`,
                    },
                    response: null,
                });
            }
            query.pos = pos;
        }
        return this.store.apiService.getV2<api.PostListBody>(
            '/collection_posts', query, headers);
    }
    key([id]) {
        const hasTenorScope = this.store.authService.hasTenorOAuthScope();
        this.console.debug('hasTenorScope', hasTenorScope);
        return `${id}-${hasTenorScope || false}`;
    }
}


export class BaseSearchDataSource extends GifResultsDataSource {
    baseCallApi(
        q: string,
        contentfilter: string,
        searchfilter: string,
        pos: string): Promise<ResponseAndBody<api.PostListBody>> {
        const query = this.addCommonParams({
            q,
            limit: LIMIT,
            contentfilter,
        });
        if (searchfilter) {
            query.searchfilter = searchfilter;
        }
        if (pos) {
            query.pos = pos;
        }
        return this.store.apiService.getSearch(query);
    }
}


export class UniversalSearchDataSource extends BaseSearchDataSource {
    callApi([q, contentfilter, universalSearchFormat],
        pos: string) {
        const searchfilter = SEARCHFILTERS[universalSearchFormat];
        return this.baseCallApi(
            q, contentfilter, searchfilter, pos);
    }
    key([q, contentfilter, universalSearchFormat]) {
        return `${q}-${contentfilter}-${universalSearchFormat}`;
    }
}


export class SearchGifsByUsernameDataSource extends GifResultsDataSource {
    callApi([username, section, ownsProfile], pos: string) {
        this.console.debug('callApi', {ownsProfile});
        let p;
        if (section === 'favorites') {
            return this.store.apiService.getUserLikedGifs(pos);
        } else {
            if (ownsProfile === true) {
                this.console.debug('returning user owned uploads');
                p = this.store.apiService.getBatchedListUploadsV2(pos);
            } else if (ownsProfile === false) {
                this.console.debug('returning public uploads');
                const query = this.addCommonParams({});
                if (pos) {
                    query.pos = pos;
                }
                query.q = `@${username}`;
                query.limit = LIMIT;
                p = this.store.apiService.getSearch(query);
            } else {
                this.console.debug('returning blank gifs body');
                return Promise.resolve({
                    body: this.getBlankData(),
                    response: null,
                });
            }
        }

        return p.then(({body, response}) => {
            body = {...body};
            body.results = [].concat(body.results).filter((result) => {
                return !isSticker(result) && !isStatic(result);
            });
            return {body, response};
        });
    }
    key([username, section, ownsProfile]) {
        let subKey = '';
        if (ownsProfile === true) {
            subKey = 'owned';
        } else if (ownsProfile === false) {
            subKey = 'public';
        } else {
            subKey = 'blank';
        }
        const k = `${username}-gifs-${section}-${subKey}`;
        // this.console.debug('key', k);
        return k;
    }
}


export class SearchStickersByUsernameDataSource extends GifResultsDataSource {
    callApi([username, section, ownsProfile], pos: string) {
        let p;

        if (ownsProfile === true) {
            p = this.store.apiService.getBatchedListUploadsV2(pos);
        } else if (ownsProfile === false) {
            const query = this.addCommonParams({});
            if (pos) {
                query.pos = pos;
            }
            query.q = `@${username}`;
            query.limit = LIMIT;
            query.searchfilter = SEARCHFILTERS.stickers;
            p = this.store.apiService.getSearch(query);
        } else {
            this.console.debug('returning blank stickers body');
            return Promise.resolve({
                body: this.getBlankData(),
                response: null,
            });
        }

        return p.then(({body, response}) => {
            body = {...body};
            body.results = [].concat(body.results).filter((result) => {
                return isSticker(result);
            });
            return {body, response};
        });
    }
    key([username, section, ownsProfile]) {
        let subKey = '';
        if (ownsProfile === true) {
            subKey = 'owned';
        } else if (ownsProfile === false) {
            subKey = 'public';
        } else {
            subKey = 'blank';
        }
        const k = `${username}-stickers-${section}-${subKey}`;
        // this.console.debug('key', k);
        return k;
    }
}


export class SearchMemesByUsernameDataSource extends GifResultsDataSource {
    callApi([username, section, ownsProfile], pos: string) {
        let p;

        if (ownsProfile === true) {
            p = this.store.apiService.getBatchedListUploadsV2(pos);
        } else if (ownsProfile === false) {
            const query = this.addCommonParams({});
            if (pos) {
                query.pos = pos;
            }
            query.q = `@${username}`;
            query.limit = LIMIT;
            query.searchfilter = SEARCHFILTERS.memes;
            p = this.store.apiService.getSearch(query);
        } else {
            this.console.debug('returning blank memes body');
            return Promise.resolve({
                body: this.getBlankData(),
                response: null,
            });
        }

        return p.then(({body, response}) => {
            body = {...body};
            body.results = [].concat(body.results).filter((result) => {
                return !isSticker(result) && isStatic(result);
            });
            return {body, response};
        });
    }
    key([username, section, ownsProfile]) {
        let subKey = '';
        if (ownsProfile === true) {
            subKey = 'owned';
        } else if (ownsProfile === false) {
            subKey = 'public';
        } else {
            subKey = 'blank';
        }
        const k = `${username}-memes-${section}-${subKey}`;
        // this.console.debug('key', k);
        return k;
    }
}


export class ProfileDataSource extends MyDataSource {
    key([username, authedUsername]) {
        username = username.toLowerCase();
        const k = `${username}-${authedUsername || ''}`;
        return k;
    }
    getBlankData() {
        const val = {
            pending: true,
            user: {
                userid: '-1',
                username: '',
                partnername: '',
                partnercta: {url: '', text: ''},
                partnerlinks: [],
                usertype: 'user',
                tagline: '',
            },
            promise: null,
        };
        val.promise = Promise.resolve(val);
        return val;
    }
    clean(val) {
        val = {
            ...this.getBlankData(),
            ...val,
        };
        if (!isPromise(val.promise)) {
            val.promise = Promise.resolve(val);
        }
        return val;
    }
    getInitial([username, authedUsername]) {
        username = username.toLowerCase();
        const val = this.getBlankData();
        if (!username) {
            return val;
        }
        this.set([username, authedUsername], val);

        const query = {username};
        const headers = new Headers();

        let p;
        const refreshImageUrl = (url) => {
            // Sometimes these URLs may 404 or have a placeholder immediately
            // after uploading. We need to update them in place if they're on
            // the page, even if the access_token is the same.
            const els = document.body.querySelectorAll(`img[src='${url}']`);
            if (els.length) {
                fetch(url).then(() => {
                    els.forEach((img: HTMLImageElement) => {
                        img.src = url;
                    });
                });
            }
        };
        const ownsProfile = this.store.authService.ownsProfile(username);
        const googleAccessToken = this.store.authService.getGoogleAccessToken();
        if (googleAccessToken && ownsProfile) {
            addAuthorizationHeaderIfPresent(headers, googleAccessToken);
            p = this.store.apiService.getV2<api.UserSingleBody>(
                '/profile', query, headers);
        } else {
            p = this.store.apiService.getV2<api.UserSingleBody>(
                '/user', query);
        }
        p = p.then(({body, response}) => {
            if (process.env.BROWSER) {
                Object.values({
                    ...body.user.avatars,
                    ...body.user.partnerbanner,
                }).forEach((url: string) => {
                    url = this.store.apiService.addAuthToImgUrlIfV2(url);
                    refreshImageUrl(url);
                });
            }
            return {body, response};
        });

        val.promise = this.store.monitor(p.then(({body, response}) => {
            body.pending = false;
            body = this.clean(body);
            this.set([username, authedUsername], body);
            return body;
        }, ({body, response}) => {
            body = body || val;
            body.pending = false;
            body = this.clean(body);
            this.set([username, authedUsername], body);
            return body;
        }));
        return val;
    }
}
